import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { useWishlist } from '@jsmdg/wishlist-library';
import { ButtonSize, WishlistButton, WishlistButtonType } from '@jsmdg/yoshi';
import { dispatchEvent } from '../../helper/EventUtils';
import { useProduct } from '../../hooks/useProduct';
import { eventTypes } from '../../types';
import { type ProductDetailContext } from '../../types/types';
import { trackRatingClicked } from '../BuyboxContainer/buyboxContainer.tracking';
import { Nudge } from '../Nudge/Nudge';
import { RatingAndParticipantsContainer } from '../RatingAndParticipantsContainer/RatingAndParticipantsContainer';
import styles from '../Buybox/Buybox.module.scss';

const messages = defineMessages({
    screenReaderTextForWishlistRemove: {
        defaultMessage: 'Von der Wunschliste entfernen',
    },
    screenReaderTextForWishlistAdd: {
        defaultMessage: 'Zur Wunschliste hinzufügen',
    },
    activeVoucherHint: {
        defaultMessage: 'Du hast den folgenden Gutschein erfolgreich hochgeladen:',
    },
});

const handleRatingClick = (): void => {
    dispatchEvent(eventTypes.RATINGS_CLICK);
    trackRatingClicked();
};

const BuyboxHeader = ({
    handleWishlistButtonClick,
}: {
    readonly handleWishlistButtonClick: () => void;
}): JSX.Element => {
    const { productId, title } = useProduct();
    const intl = useIntl();
    const { customerState, initialWishlist, redemptionInformation, tenantConfig } =
        useFragmentContext<ProductDetailContext>();
    const { isInWishlist } = useWishlist(customerState, initialWishlist);
    const isProductInWishlist = isInWishlist(productId);
    const isVoucherForProductActive = redemptionInformation?.vouchers.some(
        voucher => voucher.productId === productId,
    );
    return (
        <div className="d-flex flex-column w-100">
            <div className={classNames(styles.voucherTitle)}>
                <FormattedMessage
                    defaultMessage="{company} Gutschein"
                    values={{
                        company: tenantConfig.shareTitle,
                    }}
                />
            </div>
            {isVoucherForProductActive && (
                <div
                    className={classNames(
                        styles.activeVoucherHint,
                        'd-none d-sm-flex px-1-5x py-1x font-complementary',
                    )}
                >
                    {intl.formatMessage(messages.activeVoucherHint)}
                </div>
            )}
            <div className="d-flex w-100">
                <h1
                    id="product-headline"
                    className={classNames('theme-text-headline w-100', styles.headline)}
                >
                    {title}
                </h1>
                <WishlistButton
                    onClick={handleWishlistButtonClick}
                    isActive={isProductInWishlist}
                    size={ButtonSize.Large}
                    className={classNames(
                        styles.wishlistButtonWrapper,
                        'ml-1x flex-shrink-0 d-none d-xs-flex',
                    )}
                    a11yText={intl.formatMessage(
                        isProductInWishlist
                            ? messages.screenReaderTextForWishlistRemove
                            : messages.screenReaderTextForWishlistAdd,
                    )}
                    data-testid="wishlist-toggle-button-pdp-buybox"
                    wishlistButtonType={WishlistButtonType.WithOutline}
                />
            </div>
            <RatingAndParticipantsContainer handleRatingClick={handleRatingClick} />
            <Nudge />
        </div>
    );
};

export { BuyboxHeader };
